// Approval-database dialogs.
//
// RecordOutcomeDialog — flip one lender match to what actually happened
// (applied / approved / funded / declined), with the real amount, product and
// date. Dates are first-class: the matcher decays proven wins by recency.
//
// PastApprovalsDialog — the BULK DUMP. Operators arrive with a book of prior
// client wins; entering them all — each with its approval date — is what makes
// the matcher smart about which lenders are actually getting money out, and
// which wins are a year stale.
import { Camera, Plus, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { approvals as approvalsApi, lenderMatches as lenderMatchesApi, type LenderMatch } from "../lib/api";
import { Modal } from "./Modal";

const PRODUCTS = [
  "credit_card", "personal_loan", "line_of_credit", "term_loan",
  "sba", "equipment", "auto", "mortgage", "other",
];
const OUTCOME_STATUSES = ["applied", "approved", "funded", "declined", "dismissed"];

const today = () => new Date().toISOString().slice(0, 10);

export function RecordOutcomeDialog({
  match, onClose, onSaved,
}: {
  match: LenderMatch | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [status, setStatus] = useState("approved");
  const [amount, setAmount] = useState("");
  const [product, setProduct] = useState("");
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!match?.id) return;
    setSaving(true); setErr(null);
    try {
      await lenderMatchesApi.recordOutcome(match.id, {
        status,
        amount: amount.trim() ? amount.replace(/[$,\s]/g, "") : undefined,
        product: product || undefined,
        outcome_on: date || undefined,
        note: note.trim() || undefined,
      });
      onSaved();
      onClose();
    } catch (e: any) {
      setErr(e?.message || "Could not save the outcome.");
    } finally { setSaving(false); }
  }

  return (
    <Modal open={!!match} title={`Record outcome — ${match?.lender_name ?? ""}`} onClose={onClose}
      footer={
        <>
          <button className="ghost-button" onClick={onClose}>Cancel</button>
          <button className="primary-button" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save outcome"}</button>
        </>
      }>
      <div className="stacked-form">
        <label className="form-field">
          <span>What happened?</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {OUTCOME_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <div className="form-row-2">
          <label className="form-field">
            <span>Amount ($)</span>
            <input inputMode="numeric" placeholder="25000" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
          <label className="form-field">
            <span>Date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
        </div>
        <label className="form-field">
          <span>Product</span>
          <select value={product} onChange={(e) => setProduct(e.target.value)}>
            <option value="">—</option>
            {PRODUCTS.map((p) => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
          </select>
        </label>
        <label className="form-field">
          <span>Note</span>
          <input placeholder="e.g. countered from $25K ask, approved at $12K" value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        {err && <p className="error-text">{err}</p>}
      </div>
    </Modal>
  );
}

interface BulkRow {
  lender_name: string;
  amount: string;
  product: string;
  approved_on: string;
  status: string;
  note: string;
  fromScreenshot?: boolean;
}
const emptyRow = (): BulkRow => ({ lender_name: "", amount: "", product: "credit_card", approved_on: "", status: "approved", note: "" });

/**
 * Downscale a screenshot to ≤1600px and re-encode as a JPEG data URL — phone
 * screenshots run 3–8MB raw; the vision model doesn't need that, and smaller
 * payloads keep extraction fast.
 */
async function fileToDataUrl(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const maxSide = 1600;
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  return canvas.toDataURL("image/jpeg", 0.85);
}

export function PastApprovalsDialog({
  open, clientId, onClose, onSaved,
}: {
  open: boolean;
  clientId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [rows, setRows] = useState<BulkRow[]>([emptyRow(), emptyRow(), emptyRow()]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractMsg, setExtractMsg] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  function setRow(i: number, patch: Partial<BulkRow>) {
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  }

  async function onScreenshots(files: FileList | null) {
    if (!files || files.length === 0) return;
    setExtracting(true); setErr(null); setExtractMsg(null);
    try {
      const images: string[] = [];
      for (const f of Array.from(files).slice(0, 8)) images.push(await fileToDataUrl(f));
      const warnings: string[] = [];
      const found: BulkRow[] = [];
      // The endpoint caps at 4 images per call — batch as needed.
      for (let i = 0; i < images.length; i += 4) {
        const r = await approvalsApi.extract(clientId, images.slice(i, i + 4));
        warnings.push(...(r.warnings || []));
        for (const row of r.rows) {
          found.push({
            lender_name: row.lender_name,
            amount: row.amount != null ? String(row.amount) : "",
            product: PRODUCTS.includes(row.product) ? row.product : "other",
            approved_on: row.approved_on ?? "",
            status: ["approved", "funded", "declined"].includes(row.status) ? row.status : "approved",
            note: row.note ?? "",
            fromScreenshot: true,
          });
        }
      }
      if (found.length) {
        // Extracted rows go on top; drop untouched blank starter rows.
        setRows((rs) => [...found, ...rs.filter((r) => r.lender_name.trim())]);
      }
      const missingDates = found.filter((r) => !r.approved_on).length;
      setExtractMsg(
        [
          found.length ? `Pulled ${found.length} approval${found.length === 1 ? "" : "s"} from ${files.length} screenshot${files.length === 1 ? "" : "s"} — review before saving.` : "No approvals found in those screenshots.",
          missingDates ? `${missingDates} row(s) need the approval date filled in.` : null,
          ...warnings,
        ].filter(Boolean).join(" "),
      );
    } catch (e: any) {
      setErr(e?.message || "Screenshot extraction failed — enter the approvals manually below.");
    } finally {
      setExtracting(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function save() {
    const filled = rows.filter((r) => r.lender_name.trim());
    if (filled.length === 0) { setErr("Enter at least one approval."); return; }
    const missingDate = filled.findIndex((r) => !r.approved_on);
    if (missingDate >= 0) {
      setErr(`Row ${missingDate + 1}: the approval date is required — recency is what makes this database useful.`);
      return;
    }
    setSaving(true); setErr(null);
    try {
      await approvalsApi.bulkAdd(clientId, filled.map((r) => ({
        lender_name: r.lender_name.trim(),
        amount: r.amount.trim() ? r.amount.replace(/[$,\s]/g, "") : undefined,
        product: r.product || undefined,
        approved_on: r.approved_on,
        status: r.status,
        note: r.note.trim() || undefined,
      })));
      setRows([emptyRow(), emptyRow(), emptyRow()]);
      onSaved();
      onClose();
    } catch (e: any) {
      setErr(e?.message || "Could not save the approvals.");
    } finally { setSaving(false); }
  }

  return (
    <Modal open={open} title="Add past approvals" onClose={onClose} wide
      footer={
        <>
          <button className="ghost-button" onClick={onClose}>Cancel</button>
          <button className="primary-button" disabled={saving} onClick={save}>
            {saving ? "Saving…" : `Save ${rows.filter((r) => r.lender_name.trim()).length || ""} approval(s)`}
          </button>
        </>
      }>
      <div className="sub-panel" style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <button className="primary-button" disabled={extracting} onClick={() => fileInput.current?.click()}>
          <Camera size={15} aria-hidden="true" /> {extracting ? "Reading screenshots…" : "Upload approval screenshots"}
        </button>
        <span className="muted small" style={{ flex: "1 1 260px" }}>
          Fastest path: screenshot the approval emails / lender dashboards and drop them here — every approval
          gets pulled out automatically for you to review. Manual rows below are the backup.
        </span>
        <input ref={fileInput} type="file" accept="image/*" multiple hidden onChange={(e) => onScreenshots(e.target.files)} />
      </div>
      {extractMsg && <p className="muted small" style={{ marginBottom: 10 }}>{extractMsg}</p>}
      <p className="muted small" style={{ marginBottom: 12 }}>
        Every row needs the lender, amount, and <strong>when</strong>. Dates matter: recent wins boost a
        lender's ranking hard, year-old wins barely move it (boxes change). Unknown lenders are created
        automatically; fill in their credit box later on the Lenders page.
      </p>
      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1.2fr 1.1fr 1fr 34px", gap: 8 }}>
          {["Lender", "Amount", "Product", "Approved on", "Status", ""].map((h, i) => (
            <span key={i} className="muted small" style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</span>
          ))}
        </div>
        {rows.map((r, i) => (
          <div key={i}>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1.2fr 1.1fr 1fr 34px", gap: 8, alignItems: "center" }}>
              <label className="form-field"><input placeholder="e.g. Best Egg" value={r.lender_name} onChange={(e) => setRow(i, { lender_name: e.target.value })} /></label>
              <label className="form-field"><input inputMode="numeric" placeholder="$" value={r.amount} onChange={(e) => setRow(i, { amount: e.target.value })} /></label>
              <label className="form-field"><select value={r.product} onChange={(e) => setRow(i, { product: e.target.value })}>
                {PRODUCTS.map((p) => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
              </select></label>
              <label className="form-field">
                <input type="date" value={r.approved_on} onChange={(e) => setRow(i, { approved_on: e.target.value })} title="Approval date"
                  style={r.lender_name.trim() && !r.approved_on ? { borderColor: "var(--ne-red-bright)" } : undefined} />
              </label>
              <label className="form-field"><select value={r.status} onChange={(e) => setRow(i, { status: e.target.value })}>
                <option value="approved">approved</option>
                <option value="funded">funded</option>
                <option value="declined">declined</option>
              </select></label>
              <button className="ghost-button" title="Remove row" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </div>
            {r.fromScreenshot && (
              <p className="muted small" style={{ margin: "4px 0 0 2px" }}>
                📸 from screenshot{r.note ? ` — "${r.note}"` : ""}{!r.approved_on ? " — add the approval date" : ""}
              </p>
            )}
          </div>
        ))}
      </div>
      <button className="ghost-button" style={{ marginTop: 10 }} onClick={() => setRows((rs) => [...rs, emptyRow()])}>
        <Plus size={14} aria-hidden="true" /> Add row
      </button>
      {err && <p className="error-text" style={{ marginTop: 10 }}>{err}</p>}
    </Modal>
  );
}
